import { useState, useEffect, useCallback } from 'react';
import { getWorkflows, getAssistants, linkWorkflow, linkAssistant, unlinkAssistant, unlinkWorkflow } from '../services/apiService';
import { UnifiedAgent, Workflow, Assistant } from '../types';

/**
 * Custom hook for unified agent management
 * Provides a single interface for managing both assistants and workflows as "agents"
 * while keeping backend API calls unchanged
 */
export const useAgents = () => {
  const [agents, setAgents] = useState<UnifiedAgent[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Transform workflow data to unified agent format
   */
  const transformWorkflowToAgent = (workflow: Workflow): UnifiedAgent => ({
    id: workflow.vapi_workflow_id,
    name: workflow.name || `Workflow ${workflow.vapi_workflow_id.slice(-8)}`,
    type: 'workflow',
    displayType: 'Agent',
    vapiId: workflow.vapi_workflow_id,
    status: 'active', // Default status - could be enhanced with actual status from backend
    createdAt: new Date(workflow.created_at),
    updatedAt: new Date(workflow.updated_at),
    originalData: workflow
  });

  /**
   * Transform assistant data to unified agent format
   */
  const transformAssistantToAgent = (assistant: Assistant): UnifiedAgent => ({
    id: assistant.vapi_assistant_id,
    name: assistant.name || `Assistant ${assistant.vapi_assistant_id.slice(-8)}`,
    type: 'assistant',
    displayType: 'Agent',
    vapiId: assistant.vapi_assistant_id,
    status: 'active', // Default status - could be enhanced with actual status from backend
    createdAt: new Date(assistant.created_at),
    updatedAt: new Date(assistant.updated_at),
    originalData: assistant
  });

  /**
   * Fetch all agents (workflows and assistants) from backend
   */
  const fetchAgents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch both workflows and assistants in parallel
      const [workflowsData, assistantsData] = await Promise.all([
        getWorkflows(),
        getAssistants()
      ]);

      // Store raw data for potential direct access
      setWorkflows(workflowsData);
      setAssistants(assistantsData);

      // Transform to unified agent format
      const unifiedWorkflows = workflowsData.map(transformWorkflowToAgent);
      const unifiedAssistants = assistantsData.map(transformAssistantToAgent);

      // Combine and sort by creation date (newest first)
      const allAgents = [...unifiedWorkflows, ...unifiedAssistants].sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
      );

      setAgents(allAgents);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch agents';
      setError(errorMessage);
      console.error('Error fetching agents:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new agent (workflow or assistant)
   */
  const createAgent = useCallback(async (agentData: {
    type: 'workflow' | 'assistant';
    vapiId: string;
    name?: string;
  }) => {
    try {
      setError(null);

      if (agentData.type === 'workflow') {
        await linkWorkflow(agentData.vapiId, agentData.name || '');
      } else {
        await linkAssistant({
          vapi_assistant_id: agentData.vapiId,
          name: agentData.name || undefined
        });
      }

      // Refresh agents list
      await fetchAgents();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create agent';
      setError(errorMessage);
      console.error('Error creating agent:', err);
      return { success: false, error: errorMessage };
    }
  }, [fetchAgents]);

  /**
   * Delete an agent (supports both assistants and workflows)
   */
  const deleteAgent = useCallback(async (agent: UnifiedAgent) => {
    try {
      setError(null);

      if (agent.type === 'assistant') {
        await unlinkAssistant(agent.id);
        // Refresh agents list
        await fetchAgents();
        return { success: true };
      } else if (agent.type === 'workflow') {
        await unlinkWorkflow(agent.id);
        // Refresh agents list
        await fetchAgents();
        return { success: true };
      } else {
        const errorMessage = 'Unknown agent type';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete agent';
      setError(errorMessage);
      console.error('Error deleting agent:', err);
      return { success: false, error: errorMessage };
    }
  }, [fetchAgents]);

  /**
   * Get agents filtered by type
   */
  const getAgentsByType = useCallback((type: 'workflow' | 'assistant') => {
    return agents.filter(agent => agent.type === type);
  }, [agents]);

  /**
   * Get agent by ID
   */
  const getAgentById = useCallback((id: string) => {
    return agents.find(agent => agent.id === id);
  }, [agents]);

  /**
   * Search agents by name or ID
   */
  const searchAgents = useCallback((query: string) => {
    if (!query.trim()) return agents;

    const lowercaseQuery = query.toLowerCase();
    return agents.filter(agent =>
      agent.name.toLowerCase().includes(lowercaseQuery) ||
      agent.vapiId.toLowerCase().includes(lowercaseQuery)
    );
  }, [agents]);

  // Fetch agents on mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return {
    // Unified agent data
    agents,
    isLoading,
    error,

    // Raw data access (for backward compatibility)
    workflows,
    assistants,

    // Actions
    fetchAgents,
    createAgent,
    deleteAgent,

    // Utilities
    getAgentsByType,
    getAgentById,
    searchAgents,

    // Statistics
    totalAgents: agents.length,
    workflowCount: workflows.length,
    assistantCount: assistants.length,

    // Clear error
    clearError: () => setError(null)
  };
};

export default useAgents;